// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/mentor/services/mentorService.ts
//  Serviço de comunicação com o Mentor IA (Claude).
//  O streaming usa fetch() nativo pois axios não suporta SSE de forma nativa.
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type {
  ConversaIA,
  CriarConversaRequest,
  EnviarMensagemRequest,
  RespostaMentorResponse,
  GerarRelatorioRequest,
  RelatorioIAResponse,
  EstatisticasIA,
  SSEEvento,
} from '../types';

const BASE = '/api/mentor';

// Obtém a URL base e token do apiClient
function getBaseUrl(): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (apiClient.defaults.baseURL as string) ?? '';
}

function getToken(): string | null {
  return localStorage.getItem('udisys_token');
}

// ── REST ──────────────────────────────────────────────────────────────────

export const mentorService = {
  async criarConversa(req: CriarConversaRequest): Promise<ConversaIA> {
    const { data } = await apiClient.post<ConversaIA>(`${BASE}/conversa`, req);
    return data;
  },

  async listarConversas(idEmpresa: number, limite = 30): Promise<ConversaIA[]> {
    const { data } = await apiClient.get<ConversaIA[]>(`${BASE}/conversas`, {
      params: { idEmpresa, limite },
    });
    return data;
  },

  async buscarConversa(id: number): Promise<ConversaIA> {
    const { data } = await apiClient.get<ConversaIA>(`${BASE}/conversa/${id}`);
    return data;
  },

  async enviarMensagem(id: number, req: EnviarMensagemRequest): Promise<RespostaMentorResponse> {
    const { data } = await apiClient.post<RespostaMentorResponse>(
      `${BASE}/conversa/${id}/mensagem`, req);
    return data;
  },

  async arquivarConversa(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/conversa/${id}`);
  },

  async gerarRelatorio(req: GerarRelatorioRequest): Promise<RelatorioIAResponse> {
    const { data } = await apiClient.post<RelatorioIAResponse>(`${BASE}/relatorio/gerar`, req);
    return data;
  },

  async obterEstatisticas(idEmpresa: number): Promise<EstatisticasIA> {
    const { data } = await apiClient.get<EstatisticasIA>(`${BASE}/estatisticas`, {
      params: { idEmpresa },
    });
    return data;
  },

  // ── SSE Streaming ────────────────────────────────────────────────────────
  //
  //  Usa fetch() nativo para leitura de Server-Sent Events.
  //  Eventos: "delta" (chunk de texto), "done" (fim), "error" (erro).
  //
  //  onDelta: chamado a cada chunk de texto recebido
  //  onDone:  chamado ao final com o conversaId
  //  onError: chamado em caso de falha

  async streamMensagem(
    conversaId: number,
    req: EnviarMensagemRequest,
    onDelta: (text: string) => void,
    onDone:  (conversaId: number) => void,
    onError: (msg: string) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const token = getToken();
    const url   = `${getBaseUrl()}${BASE}/conversa/${conversaId}/stream`;

    const response = await fetch(url, {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
      },
      body:   JSON.stringify(req),
      signal,
    });

    if (!response.ok || !response.body) {
      onError(`Erro HTTP ${response.status}`);
      return;
    }

    const reader  = response.body.getReader();
    const decoder = new TextDecoder();
    let   buffer  = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      let evento: string | null = null;
      let dados:  string | null = null;

      for (const line of lines) {
        if (line.startsWith('event:')) {
          evento = line.slice(6).trim() as SSEEvento;
        } else if (line.startsWith('data:')) {
          dados = line.slice(5).trim();
        } else if (line === '' && evento && dados) {
          try {
            const parsed = JSON.parse(dados);
            if (evento === 'delta')  onDelta(parsed.text ?? '');
            if (evento === 'done')   onDone(parsed.conversaId ?? conversaId);
            if (evento === 'error')  onError(parsed.mensagem ?? 'Erro desconhecido');
          } catch { /* ignora linhas malformadas */ }
          evento = null;
          dados  = null;
        }
      }
    }
  },
};
